import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useLogin } from '@/hooks/use-auth';
import { isPlausiblePhone } from '@/lib/phone';
import { AuthLayout, PhoneField, SecretField } from './AuthLayout';

type Method = 'phone' | 'email';

/**
 * Connexion au numéro de téléphone et au code.
 *
 * Le code est le secret du compte : le code à chiffres d'un supporter, le mot
 * de passe des autres. L'e-mail reste proposé en second, pour les quelques
 * comptes créés sans numéro.
 */
export function LoginForm() {
  const [method, setMethod] = useState<Method>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { mutate: login, isPending } = useLogin();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (method === 'phone') {
      if (!isPlausiblePhone(phone)) {
        toast.error('Entrez votre numéro de téléphone complet.');
        return;
      }
      login({ phone, code });
    } else {
      login({ email, password });
    }
  };

  return (
    <AuthLayout
      title="Bon retour 👋"
      subtitle="Connectez-vous pour retrouver votre club et votre fil."
      footer={
        <div className="space-y-3 text-center text-sm text-gray-600">
          <p>
            Pas encore de compte ?{' '}
            <Link to="/register" className="font-semibold text-primary-700 hover:underline">
              Créer un compte
            </Link>
          </p>
          <p>
            Photographe ?{' '}
            <Link to="/photographe/inscription" className="font-semibold text-primary-700 hover:underline">
              Ouvrir mon espace
            </Link>
          </p>
        </div>
      }
    >
      {/* Choix de la méthode */}
      <div className="mb-6 grid grid-cols-2 rounded-lg bg-gray-100 p-1 text-sm font-medium" role="tablist">
        {(
          [
            ['phone', 'Téléphone'],
            ['email', 'E-mail'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={method === value}
            onClick={() => setMethod(value)}
            className={`rounded-md py-2 transition-colors ${
              method === value ? 'bg-white text-gray-900 shadow-card' : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        {method === 'phone' ? (
          <>
            <PhoneField id="phone" label="Numéro de téléphone" value={phone} onChange={setPhone} autoFocus />
            <SecretField
              id="code"
              label="Code"
              value={code}
              onChange={setCode}
              placeholder="••••"
              hint="Votre code secret, ou votre mot de passe si vous en avez un."
            />
          </>
        ) : (
          <>
            <div>
              <label htmlFor="email" className="label">
                Adresse e-mail
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemple@email.com"
                  className="input pl-9"
                />
              </div>
            </div>
            <SecretField
              id="password"
              label="Mot de passe"
              value={password}
              onChange={setPassword}
              aside={
                <Link to="/forgot-password" className="text-xs font-medium text-primary-700 hover:underline">
                  Mot de passe oublié ?
                </Link>
              }
            />
          </>
        )}

        <button type="submit" disabled={isPending} className="btn-primary w-full py-3 text-base">
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isPending ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>
    </AuthLayout>
  );
}
